import { useState, useRef } from 'react';
import { Search, UserPlus, UserCheck, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { useFriends } from '@/hooks/useFriends';

interface SearchResult {
  user_id: string;
  username: string;
  level: number;
  xp: number;
  is_following: boolean;
}

interface FriendSearchProps {
  open: boolean;
  onClose: () => void;
  onFollowChange?: () => void;
}

export function FriendSearch({ open, onClose, onFollowChange }: FriendSearchProps) {
  const { searchUsers, followUser, unfollowUser } = useFriends();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearch = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const data = await searchUsers(value);
      setResults(data);
      setLoading(false);
    }, 300);
  };

  const handleFollow = async (userId: string) => {
    setLoadingIds(prev => new Set(prev).add(userId));
    const ok = await followUser(userId);
    if (ok) {
      setResults(prev => prev.map(r => r.user_id === userId ? { ...r, is_following: true } : r));
      onFollowChange?.();
    }
    setLoadingIds(prev => { const s = new Set(prev); s.delete(userId); return s; });
  };

  const handleUnfollow = async (userId: string) => {
    setLoadingIds(prev => new Set(prev).add(userId));
    const ok = await unfollowUser(userId);
    if (ok) {
      setResults(prev => prev.map(r => r.user_id === userId ? { ...r, is_following: false } : r));
      onFollowChange?.();
    }
    setLoadingIds(prev => { const s = new Set(prev); s.delete(userId); return s; });
  };

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-card border-2 border-border rounded-2xl p-4 space-y-3"
    >
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by username..."
            className="pl-9 rounded-xl"
            autoFocus
          />
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {loading && (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-12 bg-muted/50 rounded-xl animate-pulse" />)}
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {results.map(user => (
            <div key={user.user_id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30">
              <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                <span className="text-xs font-display font-bold text-primary">
                  {user.username[0]?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-sm truncate">{user.username}</p>
                <p className="text-xs text-muted-foreground font-body">Level {user.level} · {user.xp} XP</p>
              </div>
              <Button
                size="sm"
                variant={user.is_following ? 'outline' : 'default'}
                onClick={() => user.is_following ? handleUnfollow(user.user_id) : handleFollow(user.user_id)}
                disabled={loadingIds.has(user.user_id)}
                className="rounded-xl shrink-0 font-display font-bold text-xs"
              >
                {user.is_following ? (
                  <><UserCheck className="w-3.5 h-3.5 mr-1" /> Following</>
                ) : (
                  <><UserPlus className="w-3.5 h-3.5 mr-1" /> Follow</>
                )}
              </Button>
            </div>
          ))}
        </div>
      )}

      {!loading && query.length >= 2 && results.length === 0 && (
        <p className="text-center text-sm text-muted-foreground font-body py-4">No users found</p>
      )}
    </motion.div>
  );
}
